import { z } from 'zod'

import {
  githubInstallSchema,
  githubProjectSchema,
} from '@scalar-org/entities/project'

import type { RequestService } from '../request'

export function githubApiFactory(requestService: RequestService) {
  /** Get a list of repositories that can be synced with a project */
  async function getRepos() {
    return await requestService.request({
      url: 'core/github/repos',
      method: 'get',
      schema: githubInstallSchema.array(),
    })
  }

  /** Get detailed repository info for a single repo */
  async function getRepoInfo(id: number) {
    return await requestService.request({
      url: `core/github/repos/${id}`,
      method: 'get',
      schema: z.object({
        name: z.string(),
        description: z.string().optional(),
        private: z.boolean(),
        url: z.string(),
        src: z.string().optional(),
      }),
    })
  }

  /** Links a github repo to a scalar github project */
  async function linkRepo(data: {
    uid: string
    repositoryId: number
    configPath: string
    branch: string
    publishOnMerge: boolean
    publishPreviews?: boolean
    prComments?: boolean
    isPrivate?: boolean
    accessGroups?: string[]
  }) {
    return await requestService.request({
      url: 'core/github/link-repo',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  /** Unlinks a github repo from a scalar github project */
  async function unlinkRepo(data: { uid: string }) {
    return await requestService.request({
      url: 'core/github/unlink-repo',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  /** Deploy a github sync project */
  async function publishRepo(githubProjectId: string) {
    return await requestService.request({
      url: 'core/publish/github',
      method: 'post',
      data: { projectId: githubProjectId },
      schema: z.object({
        publishUid: z.string(),
      }),
    })
  }

  /** Check if github project slug is available. Slugs should be unique on the same team */
  async function checkSlug(slug: string) {
    return await requestService.request({
      url: 'core/github/slug/available',
      method: 'post',
      data: { slug },
      schema: z.object({
        isAvailable: z.boolean(),
      }),
    })
  }

  /** Should update github project slug */
  async function updateSlug(uid: string, slug: string) {
    return await requestService.request({
      url: 'core/github/slug',
      method: 'post',
      data: { uid, slug },
      schema: z.null(),
    })
  }

  /** Get a github project by slug */
  async function getProject(slug: string) {
    return await requestService.request({
      url: `core/github/${slug}`,
      method: 'get',
      schema: githubProjectSchema,
    })
  }

  /** Get a github projects */
  async function getProjects() {
    return await requestService.request({
      url: 'core/github/list-projects',
      method: 'get',
      schema: githubProjectSchema.array(),
    })
  }

  /** Delete GitHub project */
  async function deleteProject(projectUid: string) {
    return await requestService.request({
      url: 'core/github/delete',
      method: 'post',
      data: { projectUid },
      schema: z.null(),
    })
  }

  /**
   * Should create a unlinked github project
   *
   * Unlinked project does not contain github repository related data
   * To publish a new build you should use cli publish command
   */
  async function createProject({
    name,
    slug,
    isPrivate,
    accessGroups,
    allowedUsers,
    allowedDomains,
    repositoryId,
  }: {
    name: string
    slug?: string
    isPrivate?: boolean
    accessGroups?: string[]
    allowedUsers?: string[]
    allowedDomains?: string[]
    repositoryId?: number
  }) {
    return await requestService.request({
      url: 'core/github/create',
      method: 'post',
      data: {
        name,
        slug,
        isPrivate,
        accessGroups,
        allowedUsers,
        allowedDomains,
        repositoryId,
      },
      schema: z.object({
        uid: z.string(),
        slug: z.string(),
      }),
    })
  }

  /** Validate github project configuration file */
  async function validateConfigFile(uid: string, configPath?: string) {
    return await requestService.request({
      url: 'core/github/config/validate',
      method: 'post',
      data: { projectId: uid, configPath },
      schema: z.union([
        z.object({
          config: z.string(),
          success: z.literal(true),
          version: z.string().optional(),
          path: z.string().optional(),
        }),
        z.object({
          config: z.string(),
          success: z.literal(false),
          version: z.string().optional(),
          error: z.string().array(),
          path: z.string().optional(),
        }),
      ]),
    })
  }

  return {
    getRepos,
    linkRepo,
    unlinkRepo,
    publishRepo,
    getRepoInfo,
    checkSlug,
    updateSlug,
    createProject,
    deleteProject,
    validateConfigFile,
    getProject,
    getProjects,
  }
}
